import React from 'react';
import {
  Alert,
  Container,
  Row,
  Col,
  Card,
  Button,
  ListGroup,
  Spinner,
} from 'react-bootstrap';
import {Link, useNavigate} from 'react-router-dom';
import axios from 'axios';
import {useAuth} from '../auth/auth-context';
import QuickStart from '../components/quick-start';
import TrustBar from '../components/trust-bar';
import type {
  JobDescriptionListInfo,
  ResumeListInfo,
} from '../models/database-models';
import {ImportFromPlatformsModal} from './import-pdf';
import {freeTrialDays} from './billing';

let hasShownHomeTrialSpotlight = false;

function HomePageHero(): React.JSX.Element {
  const {isAuthenticated} = useAuth();
  const navigate = useNavigate();
  const trialButtonRef = React.useRef<HTMLButtonElement>(null);
  const [spotlightActive, setSpotlightActive] = React.useState(false);
  const [previewMode, setPreviewMode] = React.useState<'before' | 'after'>(
    'after',
  );

  React.useEffect(() => {
    if (isAuthenticated) {
      setSpotlightActive(false);
      return;
    }

    if (
      hasShownHomeTrialSpotlight ||
      globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    let previousScrollY = globalThis.scrollY;
    let spotlightTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleScroll = () => {
      const currentScrollY = globalThis.scrollY;
      const scrollingDown = currentScrollY > previousScrollY;
      previousScrollY = currentScrollY;
      if (!scrollingDown || !trialButtonRef.current) {
        return;
      }

      const {top, bottom} = trialButtonRef.current.getBoundingClientRect();
      if (top >= globalThis.innerHeight || bottom <= 0) {
        return;
      }

      hasShownHomeTrialSpotlight = true;
      setSpotlightActive(true);
      globalThis.removeEventListener('scroll', handleScroll);
      spotlightTimeout = globalThis.setTimeout(() => {
        setSpotlightActive(false);
      }, 2000);
    };

    globalThis.addEventListener('scroll', handleScroll, {passive: true});
    return () => {
      globalThis.removeEventListener('scroll', handleScroll);
      globalThis.clearTimeout(spotlightTimeout);
    };
  }, [isAuthenticated]);

  return (
    <section className='home-hero'>
      <div className='container'>
        <div className='row align-items-center'>
          <div className='col-md-7'>
            <h1 className='display-4 fw-bold mb-3'>
              Self Driving Resume — get hired faster
            </h1>
            <p className='lead text-muted mb-2'>
              Save time, highlight the skills that matter, and increase
              interview invites.
            </p>
            <p className='lead text-muted mb-2'>
              Use AI to tailor your resume for any job posting.
            </p>
            <ul className='list-unstyled hero-benefits mb-4'>
              <li className='d-flex align-items-start'>
                <span className='benefit-icon me-2'>✓</span>
                <span>
                  ATS (Application Tracking System) optimized formatting to
                  improve resume parsing
                </span>
              </li>
              <li className='d-flex align-items-start mb-2'>
                <span className='benefit-icon me-2'>✓</span>
                <span>Multiple resume templates & export to PDF</span>
              </li>
              <li className='d-flex align-items-start mb-2'>
                <span className='benefit-icon me-2'>✓</span>
                <span>AI-tailored resume edits in seconds</span>
              </li>
            </ul>

            <div className='d-flex flex-wrap gap-2 mb-2 align-items-center'>
              {isAuthenticated ? (
                <>
                  <Button
                    size='lg'
                    variant='primary'
                    className='me-2'
                    onClick={() => {
                      void navigate('/resumes');
                    }}
                  >
                    Create or edit a resume
                  </Button>
                  <Button
                    size='lg'
                    variant='outline-secondary'
                    onClick={() => {
                      void navigate('/jobs');
                    }}
                  >
                    Add a job posting
                  </Button>
                </>
              ) : (
                <>
                  <div
                    className={`cta-callout ${spotlightActive ? 'cta-callout--spotlight' : ''}`}
                  >
                    <Button
                      ref={trialButtonRef}
                      size='lg'
                      variant='primary'
                      className='me-2 btn-cta'
                      onClick={() => {
                        void navigate('/signup');
                      }}
                    >
                      Start {freeTrialDays}-day free trial{' '}
                      <span className='cta-callout__arrow' aria-hidden>
                        →
                      </span>
                    </Button>
                  </div>
                </>
              )}
            </div>
            <div className='mt-3 small text-muted'>
              <strong>12,000+</strong> resumes created • <strong>8,000+</strong>{' '}
              jobs matched
            </div>
          </div>

          <div className='col-md-5 mt-4 mt-md-0'>
            <div className='hero-preview shadow-lg rounded'>
              <div className='hero-preview-bar' />
              <div className='hero-preview-page'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <div className='small text-muted'>AI output preview</div>
                  <div
                    className='btn-group btn-group-sm'
                    role='group'
                    aria-label='Preview mode'
                  >
                    <button
                      type='button'
                      className={`btn btn-outline-secondary ${previewMode === 'before' ? 'active' : ''}`}
                      aria-pressed={previewMode === 'before'}
                      onClick={() => {
                        setPreviewMode('before');
                      }}
                    >
                      Before
                    </button>
                    <button
                      type='button'
                      className={`btn btn-outline-secondary ${previewMode === 'after' ? 'active' : ''}`}
                      aria-pressed={previewMode === 'after'}
                      onClick={() => {
                        setPreviewMode('after');
                      }}
                    >
                      After
                    </button>
                  </div>
                </div>

                <div className='resume-block'>
                  <div className='resume-heading'>
                    <strong>Frontend Engineer — Acme Corp</strong>
                    <span className='text-muted small'> 2019–2026</span>
                  </div>

                  {previewMode === 'before' ? (
                    <ul className='resume-bullets'>
                      <li>Built features for the React app and fixed bugs</li>
                      <li>Worked with designers and backend team</li>
                      <li>Improved performance on several pages</li>
                    </ul>
                  ) : (
                    <ul className='resume-bullets'>
                      <li>
                        <span className='diff-add'>
                          Led <mark>React</mark> + <mark>TypeScript</mark>{' '}
                          migration for core UI, cutting bundle size by 28% and
                          improving TTI by 31%.
                        </span>
                        <span className='metric-tag'>-28% bundle</span>
                        <span className='metric-tag'>+31% TTI</span>
                      </li>
                      <li>
                        <span className='diff-add'>
                          Delivered A/B-tested checkout redesign, increasing
                          conversion by 17% (p&lt;.05).
                        </span>
                        <span className='metric-tag'>+17% conv.</span>
                      </li>
                      <li>
                        <span className='diff-add'>
                          Partnered with design to ship accessible components
                          (WCAG 2.1 AA), reducing UI defects 40%.
                        </span>
                        <span className='metric-tag'>-40% defects</span>
                      </li>
                    </ul>
                  )}

                  {previewMode === 'after' ? (
                    <div className='mt-2'>
                      <span className='keyword-chip'>React</span>
                      <span className='keyword-chip'>TypeScript</span>
                      <span className='keyword-chip'>A/B testing</span>
                      <span className='keyword-chip'>Accessibility</span>
                      <span className='keyword-chip'>Performance</span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className='hero-testimonial card border-0 shadow-sm mt-3'>
              <div className='card-body d-flex'>
                <div className='hero-testimonial-avatar me-3' aria-hidden>
                  A
                </div>
                <div>
                  <div className='d-flex align-items-center mb-1'>
                    <span className='badge bg-secondary me-2'>Testimonial</span>
                    <span className='text-warning small' aria-hidden>
                      ★★★★★
                    </span>
                    <span className='visually-hidden'>5 out of 5 stars</span>
                  </div>
                  <div className='fw-semibold'>
                    “I cut resume tailoring from hours to minutes and started
                    landing more callbacks. In a few weeks I had multiple
                    interviews and an offer — getting hired faster meant less
                    downtime and a quicker return to full pay.”
                  </div>
                  <div className='small text-muted'>
                    Alex — Mid-career Data Analyst — Finance → Tech — Offer in a
                    few weeks
                  </div>
                  <div className='mt-1'>
                    <span className='badge bg-secondary'>
                      More interviews, hired sooner
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='row mt-4'>
          <div className='col-12'>
            <TrustBar />
          </div>
        </div>
      </div>

      <style>{`
        .home-hero {
          background:
            radial-gradient(60% 60% at 10% -10%, rgba(var(--bs-primary-rgb, 13,110,253), 0.06), transparent 60%),
            radial-gradient(45% 45% at 100% 0%, rgba(var(--bs-info-rgb, 13,202,240), 0.04), transparent 60%),
            linear-gradient(180deg, var(--bs-body-bg), var(--bs-body-bg));
          padding: 64px 0 88px;
          border-bottom: 1px solid var(--bs-border-color);
          color: var(--bs-body-color);
        }
        @media (min-width: 768px) {
          .home-hero { padding: 96px 0 120px; }
        }
        .hero-preview {
          background: #ffffff;
          border-radius: 16px;
          overflow: hidden;
          border: 1px solid var(--bs-border-color);
          box-shadow: 0 0.25rem 1rem rgba(0,0,0,.05);
        }
        .hero-preview-bar {
          height: 32px;
          background: linear-gradient(90deg, var(--bs-gray-200, #e9ecef), var(--bs-gray-100, #f8f9fa));
        }
        .hero-preview-page {
          padding: 16px 18px 22px;
        }

        /* Emphasized benefits */
        .hero-benefits .benefit-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: rgba(var(--bs-primary-rgb, 13,110,253), .12);
          color: rgb(var(--bs-primary-rgb, 13,110,253));
          font-weight: 700;
          line-height: 1;
          margin-top: 2px; /* align with first text line */
          flex: 0 0 28px;
        }
        .hero-benefits li > span:last-child {
          color: var(--bs-body-color);
          font-weight: 500;
        }
        .hero-benefits li + li {
          margin-top: .25rem;
        }

        .hero-preview-list .benefit-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: rgba(var(--bs-primary-rgb, 13,110,253), .12);
          color: rgb(var(--bs-primary-rgb, 13,110,253));
          font-weight: 700;
          line-height: 1;
          margin-top: 2px;
          flex: 0 0 24px;
        }
        .hero-preview-list li > span:last-child {
          color: var(--bs-body-color);
          font-weight: 500;
        }

        .hero-testimonial {
          background: #ffffff;
          border: 1px solid var(--bs-border-color);
          border-radius: 12px;
        }
        .hero-testimonial-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--bs-gray-200, #e9ecef);
          color: var(--bs-body-color);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }
        /* Faux resume preview */
        .resume-block {
          border: 1px dashed var(--bs-border-color);
          border-radius: 10px;
          padding: 12px 14px;
          background: #fff;
          transition: opacity .2s ease-in-out;
        }
        .resume-heading {
          font-size: 0.95rem;
          margin-bottom: 6px;
        }
        .resume-bullets {
          padding-left: 1.1rem;
          margin: 0;
        }
        .resume-bullets li {
          margin-bottom: 6px;
          position: relative;
        }
        .resume-bullets li:last-child {
          margin-bottom: 0;
        }
        .diff-add {
          color: var(--bs-body-color);
        }
        .resume-block mark {
          background: rgba(var(--bs-primary-rgb, 13,110,253), .15);
          padding: .06rem .25rem;
          border-radius: .25rem;
        }
        .metric-tag {
          display: inline-block;
          margin-left: .4rem;
          padding: 0 .375rem;
          border-radius: .35rem;
          font-size: .72rem;
          line-height: 1.35;
          background: rgba(var(--bs-success-rgb, 25,135,84), .12);
          color: rgb(var(--bs-success-rgb, 25,135,84));
          font-weight: 600;
        }
        .keyword-chip {
          display: inline-block;
          margin: 6px 8px 0 0;
          padding: .15rem .5rem;
          border-radius: 999px;
          font-size: .72rem;
          background: rgba(var(--bs-primary-rgb, 13,110,253), .10);
          color: rgb(var(--bs-primary-rgb, 13,110,253));
          border: 1px solid rgba(var(--bs-primary-rgb, 13,110,253), .20);
          font-weight: 600;
        }
      `}</style>
    </section>
  );
}

function HomePage(): React.JSX.Element {
  const {isAuthenticated, email} = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [recentJobs, setRecentJobs] = React.useState<
    Array<{id: string; name: string}>
  >([]);
  const [recentResumes, setRecentResumes] = React.useState<
    Array<{id: string; name: string}>
  >([]);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [showImportFromPlatformsModal, setShowImportFromPlatformsModal] =
    React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    async function fetchRecents() {
      if (!isAuthenticated) {
        return;
      }

      setLoading(true);
      setError(undefined);
      try {
        const recentParameters = new URLSearchParams();
        recentParameters.set('per_page', '5');

        const [jobsResponse, resumesResponse] = await Promise.allSettled([
          axios.get<JobDescriptionListInfo[]>('/api/jobs', {
            params: recentParameters,
          }),
          axios.get<ResumeListInfo[]>('/api/resumes', {
            params: recentParameters,
          }),
        ]);

        if (!mounted) {
          return;
        }

        if (
          jobsResponse.status === 'fulfilled' &&
          Array.isArray(jobsResponse.value.data)
        ) {
          setRecentJobs(
            jobsResponse.value.data
              .slice(0, 5)
              .map(job => ({id: job.id, name: job.name})),
          );
        }

        if (
          resumesResponse.status === 'fulfilled' &&
          Array.isArray(resumesResponse.value.data)
        ) {
          setRecentResumes(
            resumesResponse.value.data
              .slice(0, 5)
              .map(resume => ({id: resume.id, name: resume.name})),
          );
        }
      } catch {
        setError('Unable to load recent activity. Try reloading later.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    void fetchRecents();
    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  return (
    <>
      <HomePageHero />
      <Container className='my-5'>
        <div className='lead'>
          {isAuthenticated ? (
            <>
              Welcome back, <strong>{email}</strong> — tailor your resume for
              each job with AI-powered suggestions.
            </>
          ) : null}
        </div>

        <Row className='mb-4'>
          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>How it works</Card.Title>
                <Card.Text>
                  Provide a job description and your resume. The system analyzes
                  both and suggests edits, reordered bullets, and keyword
                  improvements to increase relevance for the role.
                </Card.Text>
                <ListGroup variant='flush'>
                  <ListGroup.Item>
                    Extracts job keywords and required skills
                  </ListGroup.Item>
                  <ListGroup.Item>
                    Highlights relevant work experience
                  </ListGroup.Item>
                  <ListGroup.Item>
                    Generates an AI-tailored resume draft
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>
              {isAuthenticated ? (
                <Card.Footer>
                  <Button
                    onClick={() => {
                      void navigate('/help');
                    }}
                    variant='outline-primary'
                    size='sm'
                  >
                    Learn more
                  </Button>{' '}
                </Card.Footer>
              ) : null}
            </Card>
          </Col>

          <Col md={6}>
            <Card className='h-100'>
              <Card.Body>
                <Card.Title>Quick start</Card.Title>
                <QuickStart
                  isAuthenticated={isAuthenticated}
                  hasResume={recentResumes.length > 0}
                  hasJob={recentJobs.length > 0}
                  onOpenImportPlatformsModal={() => {
                    setShowImportFromPlatformsModal(true);
                  }}
                />
              </Card.Body>
              {isAuthenticated ? (
                <Card.Footer>
                  <Button
                    onClick={() => {
                      void navigate('/jobs');
                    }}
                    variant='outline-secondary'
                    size='sm'
                  >
                    My jobs
                  </Button>
                  <Button
                    onClick={() => {
                      void navigate('/resumes');
                    }}
                    variant='outline-secondary'
                    size='sm'
                  >
                    My resumes
                  </Button>{' '}
                </Card.Footer>
              ) : null}
            </Card>
          </Col>
        </Row>

        {isAuthenticated && (
          <>
            <Row className='mb-3'>
              <Col>
                <h4>Recent activity</h4>
                {loading && <Spinner animation='border' size='sm' />}
                {error && (
                  <Alert variant='warning' className='mt-2'>
                    {error}
                  </Alert>
                )}
              </Col>
            </Row>

            <Row>
              <Col md={6} className='mb-3'>
                <Card>
                  <Card.Header>
                    Jobs
                    <Button
                      className='float-end'
                      size='sm'
                      onClick={() => {
                        void navigate('/jobs');
                      }}
                    >
                      View all
                    </Button>
                  </Card.Header>
                  <ListGroup variant='flush'>
                    {recentJobs.length === 0 && !loading ? (
                      <ListGroup.Item>
                        No recent jobs. <Link to='/jobs'>Create one</Link>.
                      </ListGroup.Item>
                    ) : (
                      recentJobs.slice(0, 5).map(j => (
                        <ListGroup.Item key={j.id}>
                          <Link to={`/jobs/${j.id}`}>{j.name}</Link>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </Card>
              </Col>

              <Col md={6} className='mb-3'>
                <Card>
                  <Card.Header>
                    Resumes
                    <Button
                      className='float-end'
                      size='sm'
                      onClick={() => {
                        void navigate('/resumes');
                      }}
                    >
                      View all
                    </Button>
                  </Card.Header>
                  <ListGroup variant='flush'>
                    {recentResumes.length === 0 && !loading ? (
                      <ListGroup.Item>
                        No recent resumes. <Link to='/resumes'>Create one</Link>
                        .
                      </ListGroup.Item>
                    ) : (
                      recentResumes.slice(0, 5).map(r => (
                        <ListGroup.Item key={r.id}>
                          <Link to={`/resumes/${r.id}`}>{r.name}</Link>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {!isAuthenticated && (
          <Row className='mt-4'>
            <Col>
              <Card>
                <Card.Body className='text-center'>
                  <Card.Title>Ready to start?</Card.Title>
                  <Card.Text>
                    Create an account to begin customizing resumes with AI.
                  </Card.Text>
                  <Button
                    onClick={() => {
                      void navigate('/signup');
                    }}
                    variant='primary'
                    className='me-2 btn-signup'
                  >
                    Sign up
                  </Button>
                  <Button
                    onClick={() => {
                      void navigate('/login');
                    }}
                    variant='outline-secondary'
                  >
                    Log in
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
        <ImportFromPlatformsModal
          show={showImportFromPlatformsModal}
          onHide={() => {
            setShowImportFromPlatformsModal(false);
          }}
        />
      </Container>
    </>
  );
}

export default HomePage;
